import type {
  CatalogAction,
  CatalogCandidate,
  CatalogDecisionStep,
  CatalogEvent,
  CatalogPolicy,
  CatalogScope,
  EventCatalog,
} from "./types";

export type CatalogCategory = "events" | "policies" | "candidates" | "actions";

export const CATALOG_CATEGORIES: readonly CatalogCategory[] = [
  "events",
  "policies",
  "candidates",
  "actions",
];

export type CatalogEntry =
  | { category: "events"; item: CatalogEvent }
  | { category: "policies"; item: CatalogPolicy }
  | { category: "candidates"; item: CatalogCandidate }
  | { category: "actions"; item: CatalogAction };

export const CATEGORY_NOUN: Record<CatalogCategory, string> = {
  events: "Event",
  policies: "Policy",
  candidates: "Candidate",
  actions: "Action",
};

export const judgmentText = (step: CatalogDecisionStep): string =>
  step.judgment === "rules_only" ? "Rules only" : `Model · ${step.judgment}`;

const JUDGMENT_WORD: Record<CatalogDecisionStep["judgment"], string> = {
  rules_only: "rules-only",
  fast: "fast",
  slow: "slow",
};

/** Root step plus every nested decision in the policy, so a model-judged nested step is never hidden behind a rules-only root. */
export const policyJudgmentText = (
  policy: CatalogPolicy,
  candidates: CatalogCandidate[],
): string => {
  const root = policy.step.judgment;
  const nested = [
    ...new Set(
      candidates.flatMap((candidate) =>
        candidate.policy_id === policy.id && candidate.nested_decision
          ? [candidate.nested_decision.judgment]
          : [],
      ),
    ),
  ].sort();
  if (nested.length === 0 || (nested.length === 1 && nested[0] === root))
    return judgmentText(policy.step);
  return `Model · ${JUDGMENT_WORD[root]} root, ${nested.map((value) => JUDGMENT_WORD[value]).join("/")} nested`;
};

/** Wire contract: settled = applied + no_op + stale + failed; in-flight requests are not settled. */
export const settledRequests = (usage: CatalogAction["usage"]): number =>
  usage.applied + usage.no_op + usage.stale + usage.failed;

const PRODUCT_TEXT: Record<CatalogCandidate["produces"][number], string> = {
  action: "Action",
  nested_decision: "Nested decision",
  no_op: "No action",
  escalation: "Escalation",
};

export const producesText = (candidate: CatalogCandidate): string =>
  candidate.produces.map((product) => PRODUCT_TEXT[product]).join(" · ") ||
  "Nothing";

type EdgeKind = "handles" | "root" | "nested" | "requests" | "emits";

interface Edge {
  from: string;
  to: string;
  kind: EdgeKind;
}

export interface CatalogIndex {
  entries: Map<string, CatalogEntry>;
  /** Labels disambiguated across the whole catalog, as the table shows them. */
  labels: Map<string, EntryLabel>;
  eventIdByKind: Map<string, string>;
  actionIdByType: Map<string, string>;
  /** The decision step a candidate is listed in: its policy's root step or its parent's nested decision. */
  stepOf: Map<string, CatalogDecisionStep>;
  out: Map<string, Edge[]>;
  in: Map<string, Edge[]>;
}

const push = (map: Map<string, Edge[]>, key: string, edge: Edge) => {
  const list = map.get(key);
  if (list) {
    if (!list.some((item) => item.from === edge.from && item.to === edge.to))
      list.push(edge);
  } else map.set(key, [edge]);
};

/** Edges come from the wire ids and kinds, never by parsing an id. */
export const indexCatalog = (catalog: EventCatalog): CatalogIndex => {
  const entries = new Map<string, CatalogEntry>();
  catalog.events.forEach((item) =>
    entries.set(item.id, { category: "events", item }),
  );
  catalog.policies.forEach((item) =>
    entries.set(item.id, { category: "policies", item }),
  );
  catalog.candidates.forEach((item) =>
    entries.set(item.id, { category: "candidates", item }),
  );
  catalog.actions.forEach((item) =>
    entries.set(item.id, { category: "actions", item }),
  );
  const eventIdByKind = new Map(
    catalog.events.map((item) => [item.kind, item.id]),
  );
  const actionIdByType = new Map(
    catalog.actions.map((item) => [item.action_type, item.id]),
  );
  const out = new Map<string, Edge[]>();
  const incoming = new Map<string, Edge[]>();
  const link = (from: string, to: string | undefined, kind: EdgeKind) => {
    if (!to || from === to || !entries.has(from) || !entries.has(to)) return;
    const edge = { from, to, kind };
    push(out, from, edge);
    push(incoming, to, edge);
  };
  const stepOf = new Map<string, CatalogDecisionStep>();

  catalog.events.forEach((event) => {
    event.policy_ids.forEach((id) => link(event.id, id, "handles"));
  });
  catalog.policies.forEach((policy) => {
    policy.step.candidate_ids.forEach((id) => {
      link(policy.id, id, "root");
      stepOf.set(id, policy.step);
    });
    policy.emits_event_kinds.forEach((kind) =>
      link(policy.id, eventIdByKind.get(kind), "emits"),
    );
  });
  catalog.candidates.forEach((candidate) => {
    const nested = candidate.nested_decision;
    nested?.candidate_ids.forEach((id) => {
      link(candidate.id, id, "nested");
      stepOf.set(id, nested);
    });
    candidate.action_types.forEach((type) =>
      link(candidate.id, actionIdByType.get(type), "requests"),
    );
    candidate.emits_event_kinds.forEach((kind) =>
      link(candidate.id, eventIdByKind.get(kind), "emits"),
    );
  });
  const index: CatalogIndex = {
    entries,
    labels: new Map(),
    eventIdByKind,
    actionIdByType,
    stepOf,
    out,
    in: incoming,
  };
  index.labels = labelsFor([...entries.keys()], index);
  return index;
};

export const scopeVersionText = (scope: CatalogScope): string =>
  scope.policy_version === null
    ? "all policy versions"
    : `policy ${scope.policy_version}`;

/** Backend: in All versions, action counts include decisions under versions this build no longer registers. */
export const actionUsageScope = (scope: CatalogScope): string =>
  scope.policy_version === null
    ? "decisions under all recorded policy versions"
    : `decisions under policy ${scope.policy_version}`;

export const noPolicyText = (scope: CatalogScope): string =>
  `None in ${scope.policy_version === null ? "any" : "this"} version`;

export const eventPolicies = (
  event: CatalogEvent,
  index: CatalogIndex,
): CatalogPolicy[] =>
  event.policy_ids.flatMap((id) => {
    const entry = index.entries.get(id);
    return entry?.category === "policies" ? [entry.item] : [];
  });

/** One policy per included version: several give a per-version count or range, never a sum. */
export const rootCandidateText = (policies: CatalogPolicy[]): string | null => {
  if (policies.length === 0) return null;
  const counts = policies.map((policy) => policy.root_candidate_count);
  const low = Math.min(...counts);
  const high = Math.max(...counts);
  const text = low === high ? String(low) : `${low}–${high}`;
  return policies.length > 1 ? `${text} per version` : text;
};

export const idsFor = (keys: string[], byKey: Map<string, string>): string[] =>
  keys.flatMap((key) => {
    const id = byKey.get(key);
    return id ? [id] : [];
  });

const candidateName = (candidate: CatalogCandidate): string => {
  if (candidate.produces.includes("no_op"))
    return `No action · ${candidate.reason ?? "no reason recorded"}`;
  if (candidate.produces.includes("escalation"))
    return `Escalate · ${candidate.reason ?? "no reason recorded"}`;
  return candidate.name;
};

/** Policies and candidates are per version; events and actions are shared. */
export const entryVersion = (entry: CatalogEntry): string | null =>
  entry.category === "policies" || entry.category === "candidates"
    ? entry.item.version
    : null;

const entryName = (entry: CatalogEntry): string => {
  switch (entry.category) {
    case "events":
      return entry.item.kind;
    case "policies":
      return `${entry.item.event_kind} policy`;
    case "candidates":
      return candidateName(entry.item);
    case "actions":
      return entry.item.action_type;
  }
};

export interface EntryLabel {
  name: string;
  context: string | null;
  /** Set only where one label shows for several versions; tables show it in their Version column instead. */
  version: string | null;
}

export const labelContext = (label: EntryLabel): string | null =>
  [label.context, label.version].filter(Boolean).join(" · ") || null;

export const fullLabel = (label: EntryLabel): string => {
  const context = labelContext(label);
  return context ? `${label.name} · ${context}` : label.name;
};

const conditionText = (
  condition: CatalogCandidate["conditions"][number],
): string =>
  condition.type === "ask"
    ? `${condition.name} = ${condition.expected_choice ?? "?"}`
    : condition.name;

export interface CatalogMatch {
  key: string;
  conditions: string;
}

/**
 * A version-independent identity from wire fields, to carry a selection to
 * another version's counterpart. Labels can't: their context depends on which
 * names collide within each version. Conditions break ties only among equal keys.
 */
export const catalogMatch = (
  entry: CatalogEntry,
  index: CatalogIndex,
): CatalogMatch => {
  if (entry.category !== "candidates")
    return { key: `${entry.category} ${entryName(entry)}`, conditions: "" };
  const path: string[] = [];
  let current: CatalogCandidate | undefined = entry.item;
  while (current) {
    path.unshift(candidateName(current));
    const parent: CatalogEntry | undefined = current.parent_candidate_id
      ? index.entries.get(current.parent_candidate_id)
      : undefined;
    current = parent?.category === "candidates" ? parent.item : undefined;
  }
  return {
    key: `candidates ${entry.item.event_kind} ${path.join(" > ")}`,
    conditions: entry.item.conditions.map(conditionText).sort().join(" & "),
  };
};

/** The single entry matching `match`, or null when none or several do. */
export const counterpart = (
  match: CatalogMatch,
  entries: CatalogEntry[],
  index: CatalogIndex,
): string | null => {
  const same = entries.filter(
    (entry) => catalogMatch(entry, index).key === match.key,
  );
  const exact =
    same.length > 1
      ? same.filter(
          (entry) => catalogMatch(entry, index).conditions === match.conditions,
        )
      : same;
  return exact.length === 1 ? exact[0].item.id : null;
};

const distinguishing = (
  candidate: CatalogCandidate,
  siblings: CatalogCandidate[],
): string | null => {
  const unique = candidate.conditions.filter(
    (condition) =>
      !siblings.some((other) =>
        other.conditions.some(
          (sibling) => conditionText(sibling) === conditionText(condition),
        ),
      ),
  );
  const ask = unique.find((condition) => condition.type === "ask");
  if (ask) return conditionText(ask);
  if (unique[0]) return conditionText(unique[0]);
  if (candidate.fallback && !siblings.some((other) => other.fallback))
    return "fallback";
  const conditions = candidate.conditions.map(conditionText).sort().join(" & ");
  return conditions &&
    siblings.every(
      (other) =>
        other.conditions.map(conditionText).sort().join(" & ") !== conditions,
    )
    ? conditions
    : null;
};

const groupBy = <T>(items: T[], key: (item: T) => string) => {
  const groups = new Map<string, T[]>();
  items.forEach((item) => {
    const k = key(item);
    groups.set(k, [...(groups.get(k) ?? []), item]);
  });
  return [...groups.values()];
};

/**
 * Display labels for the ids shown together. Candidate names repeat across
 * policies and siblings, so context is added only where names collide among
 * the shown ids: event kind, then parent candidate, then the distinguishing ask,
 * within one version; the version is added last where labels still collide
 * across versions.
 */
export const labelsFor = (
  ids: readonly string[],
  index: CatalogIndex,
): Map<string, EntryLabel> => {
  const labels = new Map<string, EntryLabel>();
  const candidates: CatalogCandidate[] = [];
  ids.forEach((id) => {
    const entry = index.entries.get(id);
    if (!entry) return;
    labels.set(id, { name: entryName(entry), context: null, version: null });
    if (entry.category === "candidates") candidates.push(entry.item);
  });
  const parentName = (candidate: CatalogCandidate) => {
    const parent = candidate.parent_candidate_id
      ? index.entries.get(candidate.parent_candidate_id)
      : undefined;
    return parent?.category === "candidates"
      ? `in ${candidateName(parent.item)}`
      : "root decision";
  };
  const context = new Map<string, string[]>();
  const add = (candidate: CatalogCandidate, part: string | null) => {
    if (part)
      context.set(candidate.id, [...(context.get(candidate.id) ?? []), part]);
  };
  groupBy(candidates, (item) => item.version)
    .flatMap((sameVersion) => groupBy(sameVersion, candidateName))
    .forEach((group) => {
      if (group.length < 2) return;
      if (new Set(group.map((item) => item.event_kind)).size > 1)
        group.forEach((item) => add(item, item.event_kind));
      groupBy(group, (item) => item.event_kind).forEach((sameKind) => {
        if (sameKind.length < 2) return;
        if (new Set(sameKind.map(parentName)).size > 1)
          sameKind.forEach((item) => add(item, parentName(item)));
        groupBy(sameKind, parentName).forEach((siblings) => {
          if (siblings.length < 2) return;
          siblings.forEach((item) =>
            add(
              item,
              distinguishing(
                item,
                siblings.filter((other) => other !== item),
              ),
            ),
          );
        });
      });
    });
  context.forEach((parts, id) => {
    const label = labels.get(id);
    if (label) labels.set(id, { ...label, context: parts.join(" · ") });
  });
  const versioned = [...labels].flatMap(([id, label]) => {
    const entry = index.entries.get(id);
    const version = entry && entryVersion(entry);
    return version ? [{ id, label, version }] : [];
  });
  groupBy(versioned, (item) => fullLabel(item.label)).forEach((group) => {
    if (new Set(group.map((item) => item.version)).size < 2) return;
    group.forEach(({ id, label, version }) =>
      labels.set(id, { ...label, version }),
    );
  });
  return labels;
};

const LANES = ["event", "policy", "candidate", "sink", "handler"] as const;

type Lane = (typeof LANES)[number];

interface Place {
  /** 0 holds an event anchor's emitters; 1 the anchor's own policy tree. */
  segment: 0 | 1;
  lane: Lane;
  depth: number;
}

export interface FlowNode {
  id: string;
  role: string;
  nestedCount: number;
}

interface Flowchart {
  columns: FlowNode[][];
  /** Every catalog edge whose ends are both shown. */
  edges: { from: string; to: string }[];
}

/** All nested choices remain visible; one emission hop bounds escalation traversal. */
export const flowchart = (anchor: string, index: CatalogIndex): Flowchart => {
  const anchorEntry = index.entries.get(anchor);
  if (!anchorEntry) return { columns: [], edges: [] };
  const place = new Map<string, Place>();
  const put = (
    id: string | undefined,
    segment: Place["segment"],
    lane: Lane,
    depth = 0,
  ) => {
    if (id && index.entries.has(id) && !place.has(id))
      place.set(id, { segment, lane, depth });
  };
  const linked = (id: string, kind: EdgeKind, direction: "out" | "in") =>
    (index[direction].get(id) ?? []).flatMap((edge) =>
      edge.kind === kind ? [direction === "out" ? edge.to : edge.from] : [],
    );
  const candidate = (id: string) => {
    const entry = index.entries.get(id);
    return entry?.category === "candidates" ? entry.item : undefined;
  };
  const walk = (id: string, segment: Place["segment"], depth: number) => {
    const item = candidate(id);
    if (!item || place.has(id)) return;
    put(id, segment, "candidate", depth);
    item.action_types.forEach((type) =>
      put(index.actionIdByType.get(type), segment, "sink"),
    );
    item.emits_event_kinds.forEach((kind) =>
      put(index.eventIdByKind.get(kind), segment, "sink"),
    );
    item.nested_decision?.candidate_ids.forEach((child) =>
      walk(child, segment, depth + 1),
    );
  };
  const tree = (policyId: string, segment: Place["segment"]) => {
    const entry = index.entries.get(policyId);
    if (entry?.category !== "policies") return;
    linked(policyId, "handles", "in").forEach((id) =>
      put(id, segment, "event"),
    );
    put(policyId, segment, "policy");
    entry.item.emits_event_kinds.forEach((kind) =>
      put(index.eventIdByKind.get(kind), segment, "sink"),
    );
    entry.item.step.candidate_ids.forEach((id) => walk(id, segment, 1));
  };
  const linkedTrees = (ids: string[], segment: Place["segment"]) => {
    new Set(ids.map((id) => candidate(id)?.policy_id ?? id)).forEach((id) =>
      tree(id, segment),
    );
  };

  switch (anchorEntry.category) {
    case "policies":
      tree(anchor, 1);
      break;
    case "candidates":
      tree(anchorEntry.item.policy_id, 1);
      break;
    case "actions":
      put(anchor, 1, "sink");
      linkedTrees(linked(anchor, "requests", "in"), 1);
      break;
    case "events":
      put(anchor, 1, "event");
      linked(anchor, "handles", "out").forEach((id) => tree(id, 1));
      break;
  }
  const version = (id: string) => {
    const entry = index.entries.get(id);
    return entry ? entryVersion(entry) : null;
  };
  // Handled under the emitter's own version, as that version's scope shows it.
  [...place].forEach(([id, { lane }]) => {
    if (lane !== "sink" || index.entries.get(id)?.category !== "events") return;
    const versions = new Set(
      linked(id, "emits", "in").flatMap((from) =>
        place.has(from) ? [version(from)] : [],
      ),
    );
    linked(id, "handles", "out").forEach((policy) => {
      if (versions.has(version(policy))) put(policy, 1, "handler");
    });
  });
  if (anchorEntry.category === "events")
    linkedTrees(linked(anchor, "emits", "in"), 0);

  const emitterDepth = Math.max(
    0,
    ...[...place.values()].flatMap((item) =>
      item.segment === 0 ? [item.depth] : [],
    ),
  );
  const columnOf = new Map<string, number>();
  const rank = (id: string, { segment, lane, depth }: Place): number => {
    const start = segment === 0 ? 0 : emitterDepth + 2;
    if (lane === "event") return start;
    if (lane === "policy") return start + 1;
    if (lane === "candidate") return start + 1 + depth;
    return (
      1 +
      Math.max(
        start + 1,
        ...(index.in.get(id) ?? []).flatMap((edge) => {
          const column = columnOf.get(edge.from);
          return column === undefined ? [] : [column];
        }),
      )
    );
  };
  LANES.forEach((lane) =>
    place.forEach((at, id) => {
      if (at.lane === lane) columnOf.set(id, rank(id, at));
    }),
  );
  const role = (id: string, { lane, depth }: Place): string => {
    const entry = index.entries.get(id);
    if (!entry) return "";
    if (id === anchor)
      return `Selected ${CATEGORY_NOUN[entry.category].toLowerCase()}`;
    if (lane === "candidate")
      return depth === 1 ? "Root candidate" : "Nested candidate";
    if (lane === "sink" && entry.category === "events") return "Emitted event";
    if (lane === "handler") return "Handling policy";
    return CATEGORY_NOUN[entry.category];
  };
  const columns = new Map<number, FlowNode[]>();
  // By lane within a column, so actions never sit between nested groups.
  LANES.forEach((lane) =>
    place.forEach((at, id) => {
      if (at.lane !== lane) return;
      const children = candidate(id)?.nested_decision?.candidate_ids ?? [];
      const node: FlowNode = {
        id,
        role: role(id, at),
        nestedCount: children.length,
      };
      const column = columnOf.get(id) ?? 0;
      columns.set(column, [...(columns.get(column) ?? []), node]);
    }),
  );
  return {
    columns: [...columns]
      .sort(([left], [right]) => left - right)
      .map(([, nodes]) => nodes),
    edges: [...place.keys()].flatMap((id) =>
      (index.out.get(id) ?? []).flatMap((edge) =>
        place.has(edge.to) ? [{ from: edge.from, to: edge.to }] : [],
      ),
    ),
  };
};
